import { Component, ViewChild } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { ModalSelectProductComponent } from 'app/modal/modal-select-product/modal-select-product.component';
import { DecimalPipe } from 'app/shared/decimal.pipe';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';

declare const Mangler: any;

@Component({
	selector: 'modal-product-bulk-edit',
	templateUrl: './modal-product-bulk-edit.component.html'
})
export class ModalProductBulkEditComponent extends UIModalBaseComponent {

	@ViewChild(ModalSelectProductComponent) productSelectModal: ModalSelectProductComponent;

	bulkData = {
		products: [],
		labour: [],
		remove_labour_type_ids: [],
		accessories: [],
		remove_accessory_ids: [],
		counters: [],
		remove_counter_ids: []
	};

	step = 'operation';
	operation = null;
	products = [];

	// Data loaded once when the modal opens
	dataLoaded = false;
	productLabour: any[] = [];
	labourTypes: any[] = [];
	labourCategories: any[] = [];
	labourIndex: any = {};
	inUseLabourTypes: any[] = [];
	productAccessories: any[] = [];
	inUseAccessories: any[] = [];
	productCounters: any[] = [];
	counters: any[] = [];
	counterGroups: any[] = [];
	counterIndex: any = {};
	inUseCounters: any[] = [];

	disabled = false;

	countSelected = 0;
	countSkipped = 0;
	countAffected = 0;

	constructor(
		public app: AppService,
		private api: ApiService
	) {
		super();
	}

	open(data: any) {
		// Accepts either an array of IDs or an array of product objects (keeps backward compatibility)
		const rawIds = (data.products || []).map(p => (typeof p === 'object' && p !== null) ? p.id : p);

		this.bulkData.products = rawIds;
		this.bulkData.labour = [];
		this.bulkData.remove_labour_type_ids = [];
		this.bulkData.accessories = [];
		this.bulkData.remove_accessory_ids = [];
		this.bulkData.counters = [];
		this.bulkData.remove_counter_ids = [];

		this.products = [];
		this.operation = null;
		this.step = 'operation';
		this.dataLoaded = false;
		this.productLabour = [];
		this.labourTypes = [];
		this.labourCategories = [];
		this.labourIndex = {};
		this.inUseLabourTypes = [];
		this.productAccessories = [];
		this.inUseAccessories = [];
		this.productCounters = [];
		this.counters = [];
		this.counterGroups = [];
		this.counterIndex = {};
		this.inUseCounters = [];
		this.disabled = false;

		this.countSelected = rawIds.length;
		this.countSkipped = 0;
		this.countAffected = rawIds.length;

		this.api.product.getBulkEditData({ products: rawIds }, resp => {
			this.products = resp.products || [];
			this.productLabour = (resp.product_labour || []).map(pl => ({
				...pl,
				labour_hours: DecimalPipe.parse(pl.labour_hours)
			}));
			this.labourTypes = resp.labour_types || [];
			this.labourCategories = (resp.labour_categories || []).map(c => ({ ...c, types: [] }));
			this.labourIndex = Mangler.index(this.labourTypes, 'id');

			const cIndex = Mangler.index(this.labourCategories, 'id');
			const unassigned = { id: 0, description: 'Unassigned', types: [] };
			cIndex[0] = unassigned;
			this.labourTypes.forEach(t => {
				const c = cIndex[t.category_id || 0] || cIndex[0];
				c.types.push(t);
			});
			if (unassigned.types.length) this.labourCategories.push(unassigned);

			this.productAccessories = (resp.product_accessories || []).map(pa => ({
				...pa,
				is_own: !!+pa.is_own,
				default_quantity: DecimalPipe.parse(pa.default_quantity)
			}));

			this.productCounters = (resp.product_counters || []).map(pc => ({
				...pc,
				amount: DecimalPipe.parse(pc.amount)
			}));
			this.counters = resp.counters || [];
			this.counterGroups = (resp.counter_groups || []).map(g => ({ ...g, items: [] }));
			this.counterIndex = Mangler.index(this.counters, 'id');

			const gIndex = Mangler.index(this.counterGroups, 'id');
			const ungrouped = { id: 0, description: 'Ungrouped', items: [] };
			gIndex[0] = ungrouped;
			this.counters.forEach(c => {
				const g = gIndex[c.group_id || 0] || gIndex[0];
				g.items.push(c);
			});
			if (ungrouped.items.length) this.counterGroups.push(ungrouped);

			this.inUseLabourTypes = this.buildInUseLabourTypes();
			this.inUseAccessories = this.buildInUseAccessories();
			this.inUseCounters = this.buildInUseCounters();
			this.countSelected = this.products.length;
			this.countAffected = this.products.length;
			this.dataLoaded = true;
			this.modal.open();
		}, error => {
			this.app.notifications.showDanger(error.message);
		});
	}

	buildInUseLabourTypes() {
		const map = {};
		this.productLabour.forEach(pl => {
			if (!map[pl.labour_type_id]) {
				map[pl.labour_type_id] = {
					id: pl.labour_type_id,
					description: pl.labour_type_description,
					category_id: pl.labour_type_category_id,
					product_ids: {},
					product_count: 0,
					total_hours: 0
				};
			}
			const m = map[pl.labour_type_id];
			if (!m.product_ids[pl.product_id]) {
				m.product_ids[pl.product_id] = true;
				m.product_count += 1;
			}
			m.total_hours += DecimalPipe.parse(pl.labour_hours);
		});
		const result = [];
		Object.keys(map).forEach(k => result.push(map[k]));
		return result.sort((a: any, b: any) => (a.description || '').localeCompare(b.description || ''));
	}

	buildInUseAccessories() {
		const map = {};
		this.productAccessories.filter(pa => pa.is_own).forEach(pa => {
			if (!map[pa.accessory_id]) {
				map[pa.accessory_id] = {
					id: pa.accessory_id,
					description: pa.accessory_description,
					model: pa.accessory_model,
					sku: pa.accessory_sku || pa.accessory_manufacturer_sku,
					product_ids: {},
					product_count: 0
				};
			}
			const m = map[pa.accessory_id];
			if (!m.product_ids[pa.product_id]) {
				m.product_ids[pa.product_id] = true;
				m.product_count += 1;
			}
		});
		const result = [];
		Object.keys(map).forEach(k => result.push(map[k]));
		return result.sort((a: any, b: any) => (a.description || '').localeCompare(b.description || ''));
	}

	buildInUseCounters() {
		const map = {};
		this.productCounters.forEach(pc => {
			if (!map[pc.counter_id]) {
				map[pc.counter_id] = {
					id: pc.counter_id,
					description: pc.counter_description,
					group_id: pc.counter_group_id,
					product_ids: {},
					product_count: 0,
					total_amount: 0
				};
			}
			const m = map[pc.counter_id];
			if (!m.product_ids[pc.product_id]) {
				m.product_ids[pc.product_id] = true;
				m.product_count += 1;
			}
			m.total_amount += DecimalPipe.parse(pc.amount);
		});
		const result = [];
		Object.keys(map).forEach(k => result.push(map[k]));
		return result.sort((a: any, b: any) => (a.description || '').localeCompare(b.description || ''));
	}

	setOperation(op) {
		this.operation = op;
		this.goToNextStep();
	}

	// The step where the operation's details are picked, or null when it goes straight to the confirmation
	detailStep() {
		switch (this.operation) {
			case 'labour_add': return 'select_labour';
			case 'labour_remove_selected': return 'select_labour_types';
			case 'accessory_add': return 'select_accessories';
			case 'accessory_remove_selected': return 'select_accessory_products';
			case 'counter_add': return 'select_counters';
			case 'counter_remove_selected': return 'select_counter_types';
			default: return null;
		}
	}

	goToNextStep() {
		if (!this.operation) {
			this.step = 'operation';
		} else if (this.step === 'operation') {
			this.step = this.detailStep() || 'confirm';
		} else {
			this.step = 'confirm';
		}

		if (this.step === 'confirm') this.processIssues();
	}

	goToPreviousStep() {
		if (this.disabled || this.step === 'operation') return;

		if (this.step === 'confirm') {
			this.step = this.detailStep() || 'operation';
		} else {
			this.step = 'operation';
		}
	}

	canGoBack() {
		return this.step !== 'operation';
	}

	canGoForward() {
		switch (this.step) {
			case 'operation': return false;
			case 'select_labour': return this.bulkData.labour.some(l => l.labour_type_id && DecimalPipe.parse(l.labour_hours) > 0);
			case 'select_labour_types': return this.bulkData.remove_labour_type_ids.length > 0;
			// default_quantity is an unsigned column, so a negative would be rejected by the database
			case 'select_accessories': return this.bulkData.accessories.length > 0 && this.bulkData.accessories.every(a => DecimalPipe.parse(a.default_quantity) >= 0);
			case 'select_accessory_products': return this.bulkData.remove_accessory_ids.length > 0;
			case 'select_counters': return this.bulkData.counters.some(c => c.counter_id);
			case 'select_counter_types': return this.bulkData.remove_counter_ids.length > 0;
			case 'confirm': return false;
		}
	}

	// Labour

	updateLabour(labour) {
		const lt = this.labourIndex[labour.labour_type_id];
		labour.hours_per_day = lt ? lt.hours_per_day || 8 : 8;
		labour.currency_id = lt ? lt.currency_id || null : null;
		labour.hourly_cost = lt ? lt.hourly_cost || 0 : 0;
		labour.hourly_price = lt ? lt.hourly_price || 0 : 0;
		this.formatLabourHours();
	}

	getLabourPrice(labour) {
		const hours = DecimalPipe.parse(labour.labour_hours);
		const price = DecimalPipe.parse(labour.hourly_price);
		return hours * price;
	}

	formatLabourHours(addThousandSeparators: boolean = true) {
		this.bulkData.labour.forEach(item => {
			item.labour_hours = DecimalPipe.transform(item.labour_hours, 2, 4, addThousandSeparators);
			const lt = this.labourIndex[item.labour_type_id];
			item.hours_per_day = lt ? lt.hours_per_day || 8 : 8;
			item.labour_days = item.hours_per_day < 0.01 ? 0 : DecimalPipe.parse(item.labour_hours) / item.hours_per_day;
			item.labour_days = DecimalPipe.transform(item.labour_days, 2, 2, addThousandSeparators);
		});
	}

	labourHoursChanged(item) {
		if (item.hours_per_day < 0.01) {
			item.labour_days = 0;
		} else {
			item.labour_days = DecimalPipe.parse(item.labour_hours) / item.hours_per_day;
		}
		item.labour_days = DecimalPipe.transform(item.labour_days, 2, 2, true);
	}

	labourDaysChanged(item) {
		item.labour_hours = DecimalPipe.parse(item.labour_days) * item.hours_per_day;
		item.labour_hours = DecimalPipe.transform(item.labour_hours, 2, 4, true);
	}

	addLabour() {
		this.bulkData.labour.push({
			labour_type_id: null,
			labour_hours: 1,
			hours_per_day: 8,
			labour_days: null,
			currency_id: null,
			hourly_cost: 0,
			hourly_price: 0
		});
		this.bulkData.labour = this.bulkData.labour.slice();
		this.formatLabourHours();
	}

	deleteLabour(labour) {
		const i = this.bulkData.labour.indexOf(labour);
		if (i !== -1) {
			this.bulkData.labour.splice(i, 1);
			this.bulkData.labour = this.bulkData.labour.slice();
		}
	}

	isLabourTypeSelected(id) {
		return this.bulkData.remove_labour_type_ids.indexOf(id) !== -1;
	}

	toggleLabourTypeSelected(id) {
		const i = this.bulkData.remove_labour_type_ids.indexOf(id);
		if (i === -1) {
			this.bulkData.remove_labour_type_ids.push(id);
		} else {
			this.bulkData.remove_labour_type_ids.splice(i, 1);
		}
	}

	getLabourBefore(productId) {
		return this.productLabour.filter(pl => pl.product_id === productId);
	}

	isLabourBeingRemoved(labourTypeId) {
		if (this.operation === 'labour_remove_all') return true;
		if (this.operation === 'labour_remove_selected') {
			return this.bulkData.remove_labour_type_ids.indexOf(labourTypeId) !== -1;
		}
		return false;
	}

	getLabourAfter(productId) {
		const before = this.getLabourBefore(productId);

		if (this.operation === 'labour_remove_all') return [];

		if (this.operation === 'labour_remove_selected') {
			return before.filter(pl => !this.isLabourBeingRemoved(pl.labour_type_id));
		}

		if (this.operation === 'labour_add') {
			const after = before.map(pl => ({ ...pl, is_existing: true }));
			this.bulkData.labour
				.filter(l => l.labour_type_id && DecimalPipe.parse(l.labour_hours) > 0)
				.forEach(l => {
					const hours = DecimalPipe.parse(l.labour_hours);
					const existing = after.find(pl => pl.labour_type_id === l.labour_type_id);
					if (existing) {
						existing.labour_hours = DecimalPipe.parse(existing.labour_hours) + hours;
						existing.is_updated = true;
					} else {
						after.push({
							product_id: productId,
							labour_type_id: l.labour_type_id,
							labour_type_description: this.labourIndex[l.labour_type_id]?.description,
							labour_hours: hours,
							is_new: true
						});
					}
				});
			return after;
		}

		return before;
	}

	// Accessories

	formatAccessoryQuantities(addThousandSeparators: boolean = true) {
		this.bulkData.accessories.forEach(item => {
			item.default_quantity = DecimalPipe.transform(item.default_quantity, 0, 4, addThousandSeparators);
		});
	}

	addAccessories() {
		this.productSelectModal.open({
			is_bundle: 0,
			mode: 'multi'
		});
	}

	productSelectModalClosed(data) {
		if (!data) return;

		data.forEach(p => {
			if (this.bulkData.accessories.some(a => a.id === p.id)) {
				this.app.notifications.showDanger('Product is already an accessory.');
				return;
			}

			this.bulkData.accessories.push({
				id: p.id,
				sku: p.sku,
				manufacturer_name: p.manufacturer_name,
				manufacturer_sku: p.manufacturer_sku,
				model: p.model,
				short_description: p.short_description,
				image_url: p.image_url,
				default_quantity: p.default_quantity ?? 1
			});
		});

		this.bulkData.accessories = this.bulkData.accessories.slice();
		this.formatAccessoryQuantities();
	}

	deleteAccessory(item) {
		const i = this.bulkData.accessories.indexOf(item);
		if (i !== -1) {
			this.bulkData.accessories.splice(i, 1);
			this.bulkData.accessories = this.bulkData.accessories.slice();
		}
	}

	isAccessorySelected(id) {
		return this.bulkData.remove_accessory_ids.indexOf(id) !== -1;
	}

	toggleAccessorySelected(id) {
		const i = this.bulkData.remove_accessory_ids.indexOf(id);
		if (i === -1) {
			this.bulkData.remove_accessory_ids.push(id);
		} else {
			this.bulkData.remove_accessory_ids.splice(i, 1);
		}
	}

	// Only the organisation's own rows can be edited here - accessories a catalogue owner added are read-only
	getAccessoriesBefore(productId) {
		return this.productAccessories.filter(pa => pa.product_id === productId && pa.is_own);
	}

	hasAccessory(productId, accessoryId) {
		return this.productAccessories.some(pa => pa.product_id === productId && pa.accessory_id === accessoryId);
	}

	getAccessoriesAfter(productId) {
		const before = this.getAccessoriesBefore(productId);

		if (this.operation === 'accessory_remove_all') return [];

		if (this.operation === 'accessory_remove_selected') {
			return before.filter(pa => this.bulkData.remove_accessory_ids.indexOf(pa.accessory_id) === -1);
		}

		if (this.operation === 'accessory_add') {
			const after = before.slice();
			this.bulkData.accessories.forEach(a => {
				if (a.id === productId) return;
				if (this.hasAccessory(productId, a.id)) return;
				after.push({
					product_id: productId,
					accessory_id: a.id,
					accessory_description: a.short_description,
					default_quantity: DecimalPipe.parse(a.default_quantity),
					is_new: true
				});
			});
			return after;
		}

		return before;
	}

	// Counters

	formatCounterAmounts(addThousandSeparators: boolean = true) {
		this.bulkData.counters.forEach(item => {
			item.amount = DecimalPipe.transform(item.amount, 0, 2, addThousandSeparators);
		});
	}

	addCounter() {
		this.bulkData.counters.push({
			counter_id: null,
			amount: 1
		});
		this.bulkData.counters = this.bulkData.counters.slice();
		this.formatCounterAmounts();
	}

	deleteCounter(item) {
		const i = this.bulkData.counters.indexOf(item);
		if (i !== -1) {
			this.bulkData.counters.splice(i, 1);
			this.bulkData.counters = this.bulkData.counters.slice();
		}
	}

	isCounterSelected(id) {
		return this.bulkData.remove_counter_ids.indexOf(id) !== -1;
	}

	toggleCounterSelected(id) {
		const i = this.bulkData.remove_counter_ids.indexOf(id);
		if (i === -1) {
			this.bulkData.remove_counter_ids.push(id);
		} else {
			this.bulkData.remove_counter_ids.splice(i, 1);
		}
	}

	getCountersBefore(productId) {
		return this.productCounters.filter(pc => pc.product_id === productId);
	}

	getCountersAfter(productId) {
		const before = this.getCountersBefore(productId);

		if (this.operation === 'counter_remove_all') return [];

		if (this.operation === 'counter_remove_selected') {
			return before.filter(pc => this.bulkData.remove_counter_ids.indexOf(pc.counter_id) === -1);
		}

		if (this.operation === 'counter_add') {
			const after = before.map(pc => ({ ...pc }));
			this.bulkData.counters
				.filter(c => c.counter_id)
				.forEach(c => {
					const amount = DecimalPipe.parse(c.amount);
					const existing = after.find(pc => pc.counter_id === c.counter_id);
					if (existing) {
						existing.amount = DecimalPipe.parse(existing.amount) + amount;
						existing.is_updated = true;
					} else {
						after.push({
							product_id: productId,
							counter_id: c.counter_id,
							counter_description: this.counterIndex[c.counter_id]?.description,
							amount,
							is_new: true
						});
					}
				});
			return after;
		}

		return before;
	}

	// Confirmation

	// Removing a selection only affects products that actually have it, so the review lists a before/after count
	showBeforeAfter() {
		return this.operation === 'labour_remove_selected'
			|| this.operation === 'accessory_remove_selected'
			|| this.operation === 'counter_remove_selected';
	}

	beforeAfterLabel() {
		switch (this.operation) {
			case 'accessory_remove_selected': return 'Accessories';
			case 'counter_remove_selected': return 'Counters';
			default: return 'Labour';
		}
	}

	getBeforeCount(productId) {
		switch (this.operation) {
			case 'accessory_remove_selected': return this.getAccessoriesBefore(productId).length;
			case 'counter_remove_selected': return this.getCountersBefore(productId).length;
			default: return this.getLabourBefore(productId).length;
		}
	}

	getAfterCount(productId) {
		switch (this.operation) {
			case 'accessory_remove_selected': return this.getAccessoriesAfter(productId).length;
			case 'counter_remove_selected': return this.getCountersAfter(productId).length;
			default: return this.getLabourAfter(productId).length;
		}
	}

	processIssues() {
		this.countSelected = this.products.length;
		this.countSkipped = 0;
		this.countAffected = 0;

		this.products.forEach(p => {
			p.issues = [];

			switch (this.operation) {
				case 'labour_remove_all':
					if (!this.getLabourBefore(p.id).length) p.issues.push('No labour');
					break;

				case 'labour_remove_selected':
					if (!this.getLabourBefore(p.id).some(pl => this.isLabourBeingRemoved(pl.labour_type_id))) p.issues.push('Labour not selected');
					break;

				case 'accessory_add':
					// Products that already have every picked accessory (or are one of them) are left untouched
					if (this.getAccessoriesAfter(p.id).length === this.getAccessoriesBefore(p.id).length) p.issues.push('Accessories already added');
					break;

				case 'accessory_remove_all':
					if (!this.getAccessoriesBefore(p.id).length) p.issues.push('No accessories');
					break;

				case 'accessory_remove_selected':
					if (!this.getAccessoriesBefore(p.id).some(pa => this.isAccessorySelected(pa.accessory_id))) p.issues.push('Accessories not selected');
					break;

				case 'counter_remove_all':
					if (!this.getCountersBefore(p.id).length) p.issues.push('No counters');
					break;

				case 'counter_remove_selected':
					if (!this.getCountersBefore(p.id).some(pc => this.isCounterSelected(pc.counter_id))) p.issues.push('Counters not selected');
					break;
			}

			if (p.issues.length) this.countSkipped += 1;
			else this.countAffected += 1;
		});
	}

	productWillChange(productId) {
		const p = this.products.find(pp => pp.id === productId);
		return !p?.issues?.length;
	}

	get affectedProducts() {
		return this.products.filter(p => !p.issues?.length);
	}

	get unaffectedProducts() {
		return this.products.filter(p => p.issues?.length);
	}

	submitButton() {
		const payload: any = {
			products: this.bulkData.products,
			operation: this.operation
		};

		if (this.operation === 'labour_add') {
			this.formatLabourHours(false);
			payload.labour = this.bulkData.labour
				.filter(item => item.labour_type_id && DecimalPipe.parse(item.labour_hours) > 0)
				.map(item => ({ id: item.labour_type_id, hours: item.labour_hours }));
			this.formatLabourHours(true);
		}

		if (this.operation === 'labour_remove_selected') {
			payload.remove_labour_type_ids = this.bulkData.remove_labour_type_ids;
		}

		if (this.operation === 'accessory_add') {
			payload.accessories = this.bulkData.accessories
				.filter(item => item.id)
				.map(item => ({ id: item.id, quantity: DecimalPipe.parse(item.default_quantity) }));
		}

		if (this.operation === 'accessory_remove_selected') {
			payload.remove_accessory_ids = this.bulkData.remove_accessory_ids;
		}

		if (this.operation === 'counter_add') {
			payload.counters = this.bulkData.counters
				.filter(item => item.counter_id)
				.map(item => ({ id: item.counter_id, amount: DecimalPipe.parse(item.amount) }));
		}

		if (this.operation === 'counter_remove_selected') {
			payload.remove_counter_ids = this.bulkData.remove_counter_ids;
		}

		this.disabled = true;
		this.api.product.bulkOperation(payload, () => {
			this.disabled = false;
			this.app.notifications.showSuccess('Bulk update complete.');
			this.modal.close(true);
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}
}
